import { FormControlLabel, Switch } from '@mui/material'
import type { GridFilterItem } from '@mui/x-data-grid'
import { FilterPanelTrigger, gridFilterModelSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid'
import type { ChangeEvent } from 'react'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import GridSearchOutlined from '@/components/dataDisplay/GridSearchOutlined'
import GridToolbar from '@/components/dataDisplay/GridToolbar'
import CountryFilter from '@/components/datagrid/CountryFilter'
import MarketProgramFilter from '@/components/datagrid/MarketProgramFilter'
import type { MarketProgramType } from '@/types/marketProgramType'
import { getFilterItemValue, updateDataGridFilter } from '@/utils/datagrid/filters'

/**
 * Module augmentation is necessary to use slotProps without TypeScript errors.
 */
declare module '@mui/x-data-grid' {
  interface ToolbarPropsOverrides extends SiteDataGridToolbarProps {}
}

type SiteDataGridToolbarProps = {
  marketProgramTypes: MarketProgramType[]
  countryOptions: CountryIdentifier[]
  showCountryFilter: boolean
  showDisabledSites: boolean
  onToggleDisabledSitesClick: (value: boolean) => void
}

const MARKET_PROGRAMS_FILTER_NAME = 'marketPrograms'
const COUNTRY_FILTER_NAME = 'country'

function SitesDataGridToolbar({
  countryOptions,
  marketProgramTypes,
  showCountryFilter,
  showDisabledSites,
  onToggleDisabledSitesClick,
}: Readonly<SiteDataGridToolbarProps>) {
  const { t } = useTranslation()
  const apiRef = useGridApiContext()
  const marketProgramOptions = useMemo(
    () =>
      marketProgramTypes.map((marketProgram) => ({
        id: marketProgram,
        value: marketProgram,
        label: t(`common.market_program.${marketProgram}`),
      })),
    [marketProgramTypes],
  )

  const filterModel = useGridSelector(apiRef, gridFilterModelSelector)
  const marketProgramFilterValue = getFilterItemValue(filterModel, MARKET_PROGRAMS_FILTER_NAME) ?? ''
  const countryFilterValue = getFilterItemValue(filterModel, COUNTRY_FILTER_NAME) ?? ''

  function handleFilterChange(filterName: string, items: GridFilterItem) {
    updateDataGridFilter(apiRef, filterName, items)
  }

  function handleDisabledFilterToggle(evt: ChangeEvent<HTMLInputElement>) {
    onToggleDisabledSitesClick(evt.target.checked)
  }

  return (
    <GridToolbar
      filters={
        <>
          <GridSearchOutlined label={t('common.navigation.search')} placeholder={t('customer.search.placeholder')} />

          <MarketProgramFilter
            showAllOption
            marketProgramOptions={marketProgramOptions}
            value={marketProgramFilterValue}
            onChange={(event) => {
              handleFilterChange('marketPrograms', {
                id: 'marketPrograms',
                field: 'marketPrograms',
                operator: 'contains',
                value: event.target.value,
              })
            }}
          />

          {showCountryFilter && (
            <CountryFilter
              countryOptions={countryOptions}
              field="country"
              value={countryFilterValue}
              onChange={(items) => handleFilterChange(COUNTRY_FILTER_NAME, items[0])}
            />
          )}

          <FormControlLabel
            control={<Switch checked={showDisabledSites} onChange={handleDisabledFilterToggle} />}
            label={t('component.filter.show_disabled')}
            sx={{ m: 0 }}
          />
          <FilterPanelTrigger />
        </>
      }
    />
  )
}

export default SitesDataGridToolbar
